import {getDatabase, ref, set, push, remove, get} from 'firebase/database';

// CRUD operations for departments
export async function createDepartment(customerId, name) {
    const db = getDatabase();
    const departmentsRef = ref(db, `customers/${customerId}/departments`);
    const newDepartmentRef = push(departmentsRef);
    
    const departmentData = {
        id: newDepartmentRef.key,
        name,
        projects: {}
    };
    
    await set(newDepartmentRef, departmentData);
    return newDepartmentRef.key;
}

export async function updateDepartmentName(customerId, departmentId, name) {
    const db = getDatabase();
    const departmentRef = ref(db, `customers/${customerId}/departments/${departmentId}/name`);
    await set(departmentRef, name);
}

export async function deleteDepartment(customerId, departmentId) {
    const db = getDatabase();
    
    // Get all projects in the department
    const departmentRef = ref(db, `customers/${customerId}/departments/${departmentId}`);
    const snapshot = await get(departmentRef);
    const department = snapshot.val();
    
    if (department?.projects) {
        // Delete all workshops in each project
        for (const project of Object.values(department.projects)) {
            if (project.workshops) {
                for (const workshopId of Object.keys(project.workshops)) {
                    let workshopRef;
                    if (departmentId && project.id) {
                        workshopRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects/${project.id}/workshops/${workshopId}`);
                    } else {
                        workshopRef = ref(db, `customers/${customerId}/workshop/${workshopId}`);
                    }
                    await remove(workshopRef);
                }
            }
        }
    }
    
    // Delete the department and all its projects
    await remove(departmentRef);
}

// CRUD operations for projects
export async function createProject(customerId, departmentId, name) {
    const db = getDatabase();
    const projectsRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects`);
    const newProjectRef = push(projectsRef);
    
    const projectData = {
        id: newProjectRef.key,
        name,
        startDate: new Date().toISOString(),
        workshops: {}
    };
    
    await set(newProjectRef, projectData);
    return newProjectRef.key;
}

export async function updateProjectName(customerId, departmentId, projectId, name) {
    const db = getDatabase();
    const projectRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects/${projectId}/name`);
    await set(projectRef, name);
}

export async function deleteProject(customerId, departmentId, projectId) {
    const db = getDatabase();
    
    // Get project data to find workshops
    const projectRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects/${projectId}`);
    const snapshot = await get(projectRef);
    const project = snapshot.val();
    
    // Delete all workshops in the project
    if (project?.workshops) {
        for (const workshopId of Object.keys(project.workshops)) {
            let workshopRef;
            if (departmentId && project.id) {
                workshopRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects/${project.id}/workshops/${workshopId}`);
            } else {
                workshopRef = ref(db, `customers/${customerId}/workshop/${workshopId}`);
            }
            await remove(workshopRef);
        }
    }
    
    // Delete the project
    await remove(projectRef);
}

// Customer operations
export async function updateCustomerName(customerId, name) {
    const db = getDatabase();
    const customerRef = ref(db, `customers/${customerId}/name`);
    await set(customerRef, name);
}

// Workshop operations
export async function updateWorkshopName(customerId, departmentId, projectId, workshopId, name) {
    const db = getDatabase();
    
    // Get workshop data to find its project
    let workshopRef;
    if (departmentId && projectId) {
        workshopRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects/${projectId}/workshops/${workshopId}`);
    } else {
        workshopRef = ref(db, `customers/${customerId}/workshop/${workshopId}`);
    }
    const snapshot = await get(workshopRef);
    const workshop = snapshot.val();
    
    if (workshop?.departmentId && workshop?.projectId) {
        // Update name in both locations
        await Promise.all([
            // Update in workshops collection
            set(ref(db, `customers/${customerId}/workshop/${workshopId}/name`), name),
            // Update in project reference
            set(ref(db, `customers/${customerId}/departments/${workshop.departmentId}/projects/${workshop.projectId}/workshops/${workshopId}/name`), name)
        ]);
    } else {
        // Just update in workshops collection if no project reference found
        await set(ref(db, `customers/${customerId}/workshop/${workshopId}/name`), name);
    }
}

export async function deleteWorkshop(customerId, departmentId, projectId, workshopId) {
    const db = getDatabase();
    
    // Delete from both locations
    await Promise.all([
        // Delete from workshops collection
        remove(ref(db, `customers/${customerId}/workshop/${workshopId}`)),
        // Delete from project reference
        remove(ref(db, `customers/${customerId}/departments/${departmentId}/projects/${projectId}/workshops/${workshopId}`))
    ]);
}
